import { supabase } from '../supabaseClient'

const RUHI_TYPES = new Set(['A004', 'A008', 'E004'])  // E004 = Youth Camp
const JY_TYPES = new Set(['A002', 'E002'])            // E002 = JY Camp
const CC_TYPE = 'A001'

/**
 * Recalculates and saves progress for a list of participant IDs.
 *
 * Progress is SESSION-AUTHORITATIVE plus an explicit manual override layer
 * (migration 99). For each section/lesson:
 *
 *     done = override(section) if set, else session-derived
 *
 * - session-derived = the section appears in any of the participant's
 *   present, non-deleted sessions (attendance or assistant_ids).
 * - override = true  → force "done"     (manual add; survives session removal)
 *              false → force "not done" (manual removal; overrides a session)
 *
 * Because we rebuild from sessions (no union with prior stored progress),
 * removing a participant from a session, trimming a session's sections, or
 * deleting a session now ROLLS BACK correctly — while overrides keep manual
 * edits. Every run also recomputes the *_book/_unit/_section pointers so the
 * Activity Details tab, CSV export, etc. never go stale.
 *
 * Call after any session write (log / edit / delete) and after a manual
 * profile edit that changed overrides.
 *
 * Activity type coverage:
 *   A001               → childrens_progress / childrens_overrides
 *   A002 + E002        → jy_progress / jy_overrides
 *   A004 + A008 + E004 → ruhi_progress / ruhi_overrides
 */
export async function recalculateProgress(participantIds) {
  if (!participantIds || participantIds.length === 0) return

  // ── Gather each participant's relevant sessions (present attendance +
  //    assistant rows), excluding deleted sessions. ──────────────────────
  const { data: allAttended } = await supabase
    .from('session_attendance')
    .select(`participant_id, present,
      activity_sessions(id, activity_type, book, grade,
        units_completed, sections_completed, sections_by_unit, lesson, deleted_at)`)
    .in('participant_id', participantIds)
    .eq('present', true)

  const sessionsByPid = {}
  ;(allAttended || []).forEach(a => {
    const s = a.activity_sessions
    if (!s || s.deleted_at) return
    ;(sessionsByPid[a.participant_id] = sessionsByPid[a.participant_id] || []).push(s)
  })

  // Assistant participation (assistant_ids array on the session, not attendance).
  const { data: allAssisted } = await supabase
    .from('activity_sessions')
    .select(`id, activity_type, book, grade,
      units_completed, sections_completed, sections_by_unit, lesson, deleted_at, assistant_ids`)
    .overlaps('assistant_ids', participantIds)
    .is('deleted_at', null)
  ;(allAssisted || []).forEach(s => {
    ;(s.assistant_ids || []).forEach(pid => {
      if (!participantIds.includes(pid)) return
      const arr = (sessionsByPid[pid] = sessionsByPid[pid] || [])
      if (!arr.some(x => x.id === s.id)) arr.push(s)
    })
  })

  // ── Current progress + overrides (for completion preservation + override
  //    application). One batched read. ───────────────────────────────────
  const { data: rows } = await supabase
    .from('participants')
    .select(`id, ruhi_progress, jy_progress, childrens_progress,
             ruhi_overrides, jy_overrides, childrens_overrides`)
    .in('id', participantIds)
  const byId = Object.fromEntries((rows || []).map(r => [r.id, r]))

  await Promise.all(participantIds.map(async (pid) => {
    const row = byId[pid] || {}
    const sessions = sessionsByPid[pid] || []
    const remainingSessionIds = new Set(sessions.map(s => s.id))

    // A completed entry stays completed only if the session that completed it
    // still exists. Legacy entries with no completed_by_session are preserved.
    const keepCompleted = (prev) => {
      if (!prev?.completed) return false
      if (!prev.completed_by_session) return true
      return remainingSessionIds.has(prev.completed_by_session)
    }
    // Apply force-on / force-off overrides to a session-derived Set.
    const applyOverrides = (set, ov) => {
      Object.entries(ov || {}).forEach(([k, on]) => {
        const n = parseInt(k)
        if (on) set.add(n); else set.delete(n)
      })
      return [...set].sort((a, b) => a - b)
    }
    // For a completed entry whose detail rebuilt to empty (legacy completion,
    // backing session gone but kept), preserve the prior unit/section detail.
    const carryCompletedDetail = (entry, prev, fields) => {
      if (entry.completed && fields.every(f => {
        const v = entry[f]
        return Array.isArray(v) ? v.length === 0 : Object.keys(v || {}).length === 0
      }) && prev) {
        fields.forEach(f => { if (prev[f] != null) entry[f] = prev[f] })
      }
      if (entry.completed) {
        if (prev?.completed_by_session) entry.completed_by_session = prev.completed_by_session
        if (prev?.completed_at) entry.completed_at = prev.completed_at
      }
      return entry
    }

    const updates = {}

    // ── Ruhi (A004 / A008 / E004) ───────────────────────────────────────
    {
      const sd = {}  // { book(str): { unit(str): Set<section> } }
      sessions.filter(s => RUHI_TYPES.has(s.activity_type) && s.book).forEach(s => {
        const b = String(s.book)
        sd[b] = sd[b] || {}
        const add = (u, secs) => {
          const k = String(u)
          sd[b][k] = sd[b][k] || new Set()
          ;(secs || []).forEach(v => sd[b][k].add(v))
        }
        const units = s.units_completed || []
        if (s.sections_by_unit) Object.entries(s.sections_by_unit).forEach(([u, secs]) => add(u, secs))
        else if (units.length === 1) add(units[0], s.sections_completed)
        else if (units.length > 1) units.forEach(u => add(u, s.sections_completed))
      })

      const overrides = row.ruhi_overrides || {}  // { book: { unit: { section: bool } } }
      const prevList = row.ruhi_progress || []
      const books = new Set([...Object.keys(sd), ...Object.keys(overrides)])

      const rebuilt = [...books].map(bk => {
        const b = parseInt(bk)
        const sdUnits = sd[bk] || {}
        const ovUnits = overrides[bk] || {}
        const sections = {}
        new Set([...Object.keys(sdUnits), ...Object.keys(ovUnits)]).forEach(u => {
          const arr = applyOverrides(new Set(sdUnits[u] ? [...sdUnits[u]] : []), ovUnits[u])
          if (arr.length) sections[u] = arr
        })
        const units = Object.keys(sections).map(Number).sort((a, b) => a - b)
        const prev = prevList.find(e => e.book === b)
        return carryCompletedDetail({ book: b, units, sections, completed: keepCompleted(prev) },
          prev, ['units', 'sections'])
      }).filter(e => e.units.length > 0 || e.completed).sort((a, b) => a.book - b.book)

      const active = rebuilt.filter(e => !e.completed)
      const highBook = active.length ? Math.max(...active.map(e => e.book)) : null
      const he = highBook ? active.find(e => e.book === highBook) : null
      const highUnit = he?.units?.length ? Math.max(...he.units) : null
      const highSecs = highUnit ? (he.sections[String(highUnit)] || []) : []
      updates.ruhi_progress = rebuilt
      updates.ruhi_book = highBook
      updates.ruhi_unit = highUnit
      updates.ruhi_section = highSecs.length ? Math.max(...highSecs) : null
    }

    // ── Junior Youth (A002 / E002) ──────────────────────────────────────
    {
      const sd = {}  // { book(str): Set<section> }
      sessions.filter(s => JY_TYPES.has(s.activity_type) && s.book).forEach(s => {
        const b = String(s.book)
        sd[b] = sd[b] || new Set()
        ;(s.sections_completed || []).forEach(v => sd[b].add(v))
      })
      const overrides = row.jy_overrides || {}  // { book: { section: bool } }
      const prevList = row.jy_progress || []
      const books = new Set([...Object.keys(sd), ...Object.keys(overrides)])

      const rebuilt = [...books].map(bk => {
        const b = parseInt(bk)
        const sections = applyOverrides(new Set(sd[bk] ? [...sd[bk]] : []), overrides[bk])
        const prev = prevList.find(e => e.book === b)
        return carryCompletedDetail({ book: b, sections, completed: keepCompleted(prev) }, prev, ['sections'])
      }).filter(e => e.sections.length > 0 || e.completed).sort((a, b) => a.book - b.book)

      const active = rebuilt.filter(e => !e.completed)
      const highBook = active.length ? Math.max(...active.map(e => e.book)) : null
      const he = highBook ? active.find(e => e.book === highBook) : null
      updates.jy_progress = rebuilt
      updates.jy_book = highBook
      updates.jy_section = he?.sections?.length ? Math.max(...he.sections) : null
    }

    // ── Children's Class (A001) ─────────────────────────────────────────
    {
      const sd = {}  // { grade(str): Set<lesson> }
      sessions.filter(s => s.activity_type === CC_TYPE && s.grade).forEach(s => {
        const g = String(s.grade)
        sd[g] = sd[g] || new Set()
        ;(s.sections_completed || []).forEach(v => sd[g].add(v))
        if ((s.sections_completed || []).length === 0 && s.lesson) sd[g].add(s.lesson)
      })
      const overrides = row.childrens_overrides || {}  // { grade: { lesson: bool } }
      const prevList = row.childrens_progress || []
      const grades = new Set([...Object.keys(sd), ...Object.keys(overrides)])

      const rebuilt = [...grades].map(gk => {
        const g = parseInt(gk)
        const lessons = applyOverrides(new Set(sd[gk] ? [...sd[gk]] : []), overrides[gk])
        const prev = prevList.find(e => e.grade === g)
        const entry = carryCompletedDetail(
          { grade: g, lesson: lessons.length ? Math.max(...lessons) : null, lessons, completed: keepCompleted(prev) },
          prev, ['lessons'])
        if (!entry.lesson && entry.lessons?.length) entry.lesson = Math.max(...entry.lessons)
        return entry
      }).filter(e => (e.lessons && e.lessons.length > 0) || e.completed).sort((a, b) => a.grade - b.grade)

      const active = rebuilt.filter(e => !e.completed)
      const highGrade = active.length ? Math.max(...active.map(e => e.grade)) : null
      const he = highGrade ? active.find(e => e.grade === highGrade) : null
      updates.childrens_progress = rebuilt
      updates.childrens_grade = highGrade
      updates.childrens_lesson = he?.lesson || null
    }

    await supabase.from('participants').update(updates).eq('id', pid)
  }))
}

/**
 * Sets/clears a single manual override and recomputes the participant's
 * progress. `value` is true (force-on), false (force-off), or null (clear).
 * `kind` is 'ruhi' | 'jy' | 'childrens'. `path` is the key chain:
 *   ruhi:      [book, unit, section]
 *   jy:        [book, section]
 *   childrens: [grade, lesson]
 */
export async function setProgressOverride(participantId, kind, path, value) {
  const col = `${kind}_overrides`
  const { data: row } = await supabase
    .from('participants').select(col).eq('id', participantId).single()
  const overrides = (row && row[col]) || {}

  // Walk/create the nested path, set or delete the leaf.
  let node = overrides
  for (let i = 0; i < path.length - 1; i++) {
    const k = String(path[i])
    node[k] = node[k] || {}
    node = node[k]
  }
  const leaf = String(path[path.length - 1])
  if (value === null || value === undefined) delete node[leaf]
  else node[leaf] = value

  // Prune empty branches so the column stays tidy.
  const prune = (obj) => {
    Object.keys(obj).forEach(k => {
      if (obj[k] && typeof obj[k] === 'object') {
        prune(obj[k])
        if (Object.keys(obj[k]).length === 0) delete obj[k]
      }
    })
  }
  prune(overrides)

  await supabase.from('participants').update({ [col]: overrides }).eq('id', participantId)
  await recalculateProgress([participantId])
}

/**
 * When a session is saved, any section a participant is NEWLY logged as
 * completing (present in the new set, absent from the old set) clears a stale
 * force-OFF override for that section — a fresh completion wins. Editing a
 * session that already had the section does NOT clear it. Force-on overrides
 * are left intact (a manual "done" survives).
 *
 * `kind` is 'ruhi' | 'jy' | 'childrens'.
 * `newlyCompleted` maps participantId → array of override paths newly added,
 *   e.g. ruhi [[book,unit,section], ...]; jy [[book,section]]; cc [[grade,lesson]].
 */
export async function clearForceOffForNewlyCompleted(kind, newlyCompleted) {
  const pids = Object.keys(newlyCompleted || {}).filter(pid => (newlyCompleted[pid] || []).length)
  if (pids.length === 0) return
  const col = `${kind}_overrides`
  const { data: rows } = await supabase.from('participants').select(`id, ${col}`).in('id', pids)
  await Promise.all((rows || []).map(async (row) => {
    const overrides = row[col] || {}
    let changed = false
    ;(newlyCompleted[row.id] || []).forEach(path => {
      let node = overrides
      for (let i = 0; i < path.length - 1 && node; i++) node = node[String(path[i])]
      const leaf = String(path[path.length - 1])
      if (node && node[leaf] === false) { delete node[leaf]; changed = true }
    })
    if (changed) await supabase.from('participants').update({ [col]: overrides }).eq('id', row.id)
  }))
}

/**
 * The participant's SESSION-DERIVED completion sets (overrides ignored).
 * Used by the profile editor to diff a manual edit against what sessions back,
 * so it can set the minimal force-on / force-off overrides.
 *   { ruhi: {book:{unit:Set}}, jy: {book:Set}, cc: {grade:Set} }
 */
export async function getSessionDerivedProgress(participantId) {
  const { data: att } = await supabase
    .from('session_attendance')
    .select(`present, activity_sessions(id, activity_type, book, grade,
      units_completed, sections_completed, sections_by_unit, lesson, deleted_at)`)
    .eq('participant_id', participantId).eq('present', true)
  const sessions = (att || []).map(a => a.activity_sessions).filter(s => s && !s.deleted_at)
  const { data: ass } = await supabase
    .from('activity_sessions')
    .select(`id, activity_type, book, grade, units_completed, sections_completed,
      sections_by_unit, lesson, deleted_at, assistant_ids`)
    .contains('assistant_ids', [participantId]).is('deleted_at', null)
  ;(ass || []).forEach(s => { if (!sessions.some(x => x.id === s.id)) sessions.push(s) })

  const ruhi = {}, jy = {}, cc = {}
  sessions.forEach(s => {
    if (RUHI_TYPES.has(s.activity_type) && s.book) {
      const b = String(s.book); ruhi[b] = ruhi[b] || {}
      const add = (u, secs) => { const k = String(u); ruhi[b][k] = ruhi[b][k] || new Set(); (secs || []).forEach(v => ruhi[b][k].add(v)) }
      const units = s.units_completed || []
      if (s.sections_by_unit) Object.entries(s.sections_by_unit).forEach(([u, secs]) => add(u, secs))
      else if (units.length === 1) add(units[0], s.sections_completed)
      else if (units.length > 1) units.forEach(u => add(u, s.sections_completed))
    } else if (JY_TYPES.has(s.activity_type) && s.book) {
      const b = String(s.book); jy[b] = jy[b] || new Set(); (s.sections_completed || []).forEach(v => jy[b].add(v))
    } else if (s.activity_type === CC_TYPE && s.grade) {
      const g = String(s.grade); cc[g] = cc[g] || new Set()
      ;(s.sections_completed || []).forEach(v => cc[g].add(v))
      if ((s.sections_completed || []).length === 0 && s.lesson) cc[g].add(s.lesson)
    }
  })
  return { ruhi, jy, cc }
}

// Diff a desired Set against a session-derived Set over 1..total and write
// force-on / force-off / clear into the override map `node` (keyed by string).
function diffIntoOverride(node, desiredSet, derivedSet, total) {
  const maxN = Math.max(total || 0, ...desiredSet, ...derivedSet,
    ...Object.keys(node).map(Number), 0)
  for (let n = 1; n <= maxN; n++) {
    const desired = desiredSet.has(n)
    const derived = derivedSet.has(n)
    if (desired && !derived) node[String(n)] = true
    else if (!desired && derived) node[String(n)] = false
    else delete node[String(n)]
  }
}

const pruneEmpty = (obj) => {
  Object.keys(obj).forEach(k => {
    if (obj[k] && typeof obj[k] === 'object') {
      pruneEmpty(obj[k])
      if (Object.keys(obj[k]).length === 0) delete obj[k]
    }
  })
}

/**
 * Apply a manual Ruhi edit for ONE book. `desiredByUnit` is {unit: [sections]}
 * (the state the user set in the profile editor); `sectionsPerUnit` is the
 * book's section counts (RUHI_BOOKS). Sets minimal overrides vs sessions, then
 * recomputes.
 */
export async function applyManualRuhiEdit(participantId, book, desiredByUnit, sectionsPerUnit) {
  const derived = (await getSessionDerivedProgress(participantId)).ruhi[String(book)] || {}
  const { data: row } = await supabase.from('participants').select('ruhi_overrides').eq('id', participantId).single()
  const overrides = row?.ruhi_overrides || {}
  const bk = String(book); overrides[bk] = overrides[bk] || {}
  const units = new Set([...Object.keys(desiredByUnit || {}), ...Object.keys(derived), ...Object.keys(overrides[bk])])
  units.forEach(u => {
    overrides[bk][u] = overrides[bk][u] || {}
    const desiredSet = new Set((desiredByUnit?.[u] ?? desiredByUnit?.[parseInt(u)] ?? []).map(Number))
    diffIntoOverride(overrides[bk][u], desiredSet, derived[u] || new Set(), sectionsPerUnit?.[parseInt(u)] || 0)
  })
  pruneEmpty(overrides)
  await supabase.from('participants').update({ ruhi_overrides: overrides }).eq('id', participantId)
  await recalculateProgress([participantId])
}

/** Apply a manual JY edit for ONE book. `desired` = [sections]. */
export async function applyManualJyEdit(participantId, book, desired, totalSections) {
  const derived = (await getSessionDerivedProgress(participantId)).jy[String(book)] || new Set()
  const { data: row } = await supabase.from('participants').select('jy_overrides').eq('id', participantId).single()
  const overrides = row?.jy_overrides || {}
  const bk = String(book); overrides[bk] = overrides[bk] || {}
  diffIntoOverride(overrides[bk], new Set((desired || []).map(Number)), derived, totalSections || 0)
  pruneEmpty(overrides)
  await supabase.from('participants').update({ jy_overrides: overrides }).eq('id', participantId)
  await recalculateProgress([participantId])
}

/** Apply a manual Children's edit for ONE grade. `desired` = [lessons]. */
export async function applyManualChildrensEdit(participantId, grade, desired, totalLessons) {
  const derived = (await getSessionDerivedProgress(participantId)).cc[String(grade)] || new Set()
  const { data: row } = await supabase.from('participants').select('childrens_overrides').eq('id', participantId).single()
  const overrides = row?.childrens_overrides || {}
  const gk = String(grade); overrides[gk] = overrides[gk] || {}
  diffIntoOverride(overrides[gk], new Set((desired || []).map(Number)), derived, totalLessons || 0)
  pruneEmpty(overrides)
  await supabase.from('participants').update({ childrens_overrides: overrides }).eq('id', participantId)
  await recalculateProgress([participantId])
}

/**
 * Set/clear the whole-book (or grade) "completed" flag on a manual progress
 * entry and recompute the pointers. Used by the profile editor's "completed"
 * checkbox. kind = 'ruhi' | 'jy' | 'childrens'; key = book number (or grade).
 * A manually-completed entry has no completed_by_session, so the recalc's
 * legacy-completed path keeps it across future runs.
 */
export async function setBookCompleted(participantId, kind, key, completed) {
  const col = `${kind}_progress`
  const idField = kind === 'childrens' ? 'grade' : 'book'
  const { data: row } = await supabase.from('participants').select(col).eq('id', participantId).single()
  const list = (row && row[col]) || []
  const idx = list.findIndex(e => e[idField] === parseInt(key))
  if (idx < 0) return
  if (completed) {
    list[idx] = { ...list[idx], completed: true, completed_at: list[idx].completed_at || new Date().toISOString() }
  } else {
    list[idx] = { ...list[idx], completed: false }
    delete list[idx].completed_at
    delete list[idx].completed_by_session
  }
  const active = list.filter(e => !e.completed)
  const updates = { [col]: list }
  if (kind === 'ruhi') {
    const hb = active.length ? Math.max(...active.map(e => e.book)) : null
    const he = hb ? active.find(e => e.book === hb) : null
    const hu = he?.units?.length ? Math.max(...he.units) : null
    updates.ruhi_book = hb
    updates.ruhi_unit = hu
    updates.ruhi_section = hu && he.sections?.[String(hu)]?.length ? Math.max(...he.sections[String(hu)]) : null
  } else if (kind === 'jy') {
    const hb = active.length ? Math.max(...active.map(e => e.book)) : null
    const he = hb ? active.find(e => e.book === hb) : null
    updates.jy_book = hb
    updates.jy_section = he?.sections?.length ? Math.max(...he.sections) : null
  } else {
    const hg = active.length ? Math.max(...active.map(e => e.grade)) : null
    const he = hg ? active.find(e => e.grade === hg) : null
    updates.childrens_grade = hg
    updates.childrens_lesson = he?.lesson || null
  }
  await supabase.from('participants').update(updates).eq('id', participantId)
}
